import React, { useEffect, useState, VFC } from 'react'
import styles from '../../styles/front/form/Form.module.sass'
import CustomFormContents from '../common/CustomFormContents'
import FormTemplate from '../form/Common'
import ScrollError from '../form/ScrollError'
import DividedDatePicker from './DividedDatePicker'
import ExitIntentPopup from './ExitIntentPopup'

type Prefecture = {
  prefecture_name: string
  prefecture_code: string
}

type EventSchedule = {
  id: string
  started_at: string
  finished_at: string
}

type ClosedSchedule = {
  started_at: string
  finished_at: string
}

type Event = {
  id: string
  holding_at: string
  finished_at: string
  event_type: string
  open_time: string
  close_time: string
  classification: string
  schedule_type: string
  reservation_time_type: string
  closed_wday: string
  show_flexible_schedule_option: boolean
  reservation_slot_interval_minutes: number
}

type ApplicantForm = {
  event_id: string
  event_type: string
  extra_request: string
  hope_datetime1: string
  hope_datetime2: string
  name: string
  name_kana: string
  number_of_adult: number
  number_of_children: number
  prefecture_code: string
  email: string
  tel: string
  zipcode: string
  address: string
  city_name: string
  classification: string
  inquiry_store?: string
  is_request_online?: boolean
  is_flexible_schedule?: boolean | string
  extra_item_answer?: any
}

type Stores = {
  id: number
  city_address: string
  name: string
}

type CustomContentLabel = {
  label: string
}

type CustomContents = {
  form_type: string
  title: string
  pretty_validate_type: { [key: string]: boolean }
  labels: CustomContentLabel[]
}

type ApplicantFormSetting = {
  is_show_store: boolean
  is_request_online: boolean
  is_show_number_of_adult: boolean
  is_show_number_of_children: boolean
  is_show_name_kana: boolean
  placeholder_text: string
  is_show_applicant_form_on_detail_page: boolean
  is_required_hope_datetime2: boolean
  is_show_address: boolean
  is_show_company_name: boolean
  is_show_job_title: boolean
}

type Builder = {
  required_applicant_hope_datetime2: boolean
  theme_color: string
}

type ExitIntentPopup = {
  title: string
  text: string
  imageUrl: string
  imageAlt: string
}

type ApplicantFormProps = {
  prefectures: Prefecture[]
  applicant_form: ApplicantForm
  errors?: { [key: string]: string[] }
  terms_path: string
  privacy_path: string
  event: Event
  schedules: EventSchedule[]
  closed_schedules: ClosedSchedule[]
  stores: Stores[]
  inquiry_store: Stores
  custom_contents: CustomContents[]
  applicant_form_setting?: ApplicantFormSetting
  builder: Builder
  is_consultation_event: boolean
  exit_intent_popup?: ExitIntentPopup
  modal_base_element?: string
}

const toBoolean = (value?: boolean | string): boolean => value === true || value === 'true'

const WEEKDAYS = [
  { 0: '日曜日' },
  { 1: '月曜日' },
  { 2: '火曜日' },
  { 3: '水曜日' },
  { 4: '木曜日' },
  { 5: '金曜日' },
  { 6: '土曜日' },
]

// event_detail_applicant_error_handler.jsでerrorTextのクラスを取得するために定義
export const getErrorClassName = () => styles.errorText

const ApplicantForm: VFC<ApplicantFormProps> = (props: ApplicantFormProps) => {
  const {
    prefectures,
    applicant_form = null,
    errors,
    terms_path,
    privacy_path,
    event,
    stores = null,
    custom_contents,
    applicant_form_setting,
    builder,
    inquiry_store = null,
    is_consultation_event,
    exit_intent_popup = null,
    modal_base_element = null,
    schedules = [],
    closed_schedules = [],
  } = props

  const formattedInputStartDate = (baseDateTime: string) => {
    const date = new Date(baseDateTime)
    const time = new Date()
    time.setHours(date.getHours())
    time.setMinutes(date.getMinutes())
    return time
  }

  const [inquiryStore, setInquiryStore] = useState<string>('')
  const [hopeDateTime1, setHopeDateTime1] = useState<string>(applicant_form?.hope_datetime1 ?? '')
  const [hopeDateTime2, setHopeDateTime2] = useState<string>(applicant_form?.hope_datetime2 ?? '')
  const [isFlexibleSchedule, setIsFlexibleSchedule] = useState<boolean>(toBoolean(applicant_form?.is_flexible_schedule))
  const [numberOfAdult, setNumberOfAdult] = useState<number>(applicant_form?.number_of_adult ?? 0)
  const [numberOfChildren, setNumberOfChildren] = useState<number>(applicant_form?.number_of_children ?? undefined)
  const [isRequestOnline, setIsRequestOnline] = useState<boolean>(applicant_form?.is_request_online ?? false)
  const [displayStores, setDisplayStores] = useState([])
  const [selectedDateTime1, setSelectedDateTime1] = useState<string>(applicant_form?.hope_datetime1 ?? '')
  const [selectedDateTime2, setSelectedDateTime2] = useState<string>(applicant_form?.hope_datetime2 ?? '')
  const [schedulesForHopeDateTime2, setSchedulesForHopeDateTime2] = useState<EventSchedule[]>([])
  const [hopeDate1Filled, setHopeDate1Filled] = useState<boolean>(false)
  const [hopeDate2Filled, setHopeDate2Filled] = useState<boolean>(false)
  const [hopeDate1, setHopeDate1] = useState<string>(applicant_form?.hope_datetime1 ?? '')
  const [hopeDate2, setHopeDate2] = useState<string>(applicant_form?.hope_datetime2 ?? '')

  const numberOfAdultOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  const numberOfChildrenOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

  const [isValidForm, setIsValidForm] = useState(errors && checkIsValidForm())
  const [isValidCustomContents, setIsValidCustomContents] = useState(checkIsValidCustomForm())
  const [isValidApplicantParams, setIsValidApplicantParams] = useState(false)

  const isHopeDateTime2Used = (event) => {
    if (event.schedule_type === 'specified_date' && event.reservation_time_type === 'flexible') {
      return true
    } else if (
      event.schedule_type === 'specified_date' &&
      event.reservation_time_type === 'fixed' &&
      schedules.length > 1
    ) {
      return true
    } else if (event.schedule_type === 'long_term') {
      return true
    }
    return false
  }

  const requiredDateTime2 = () => {
    if (!isHopeDateTime2Used(event)) {
      return false
    }
    if (applicant_form_setting) {
      return applicant_form_setting.is_required_hope_datetime2
    } else {
      return builder.required_applicant_hope_datetime2
    }
  }

  function checkIsValidForm() {
    const errorKeys = Object.keys(errors!)
    const baseFormKeys = ['name', 'tel', 'email']
    const addressFormKeys = ['zipcode', 'prefecture_code', 'city_name', 'address']

    // フォームに必要なフィールドを設定
    let formKeys = [...baseFormKeys]

    // フリガナが表示される場合は追加
    if (applicant_form_setting?.is_show_name_kana) {
      formKeys.push('name_kana')
    }

    // 住所が表示される場合は追加
    if (applicant_form_setting?.is_show_address) {
      formKeys = [...formKeys, ...addressFormKeys]
    }

    return !formKeys.some((el) => errorKeys.includes(el))
  }

  function checkIsValidCustomForm() {
    if (errors) {
      if (errors.extra_item_answer) return false
      return true
    }
    if (custom_contents && custom_contents.length > 0) return false
    return true
  }

  useEffect(() => {
    let storeList: string[] = []
    if (stores) {
      stores.forEach((store) => {
        const address = `${store.city_address ? '（' + store.city_address + '）' : ''}`
        const displayStoreName = `${store.name}${address}`
        storeList.push(displayStoreName)
        if (applicant_form && applicant_form.inquiry_store) {
          setInquiryStore(applicant_form.inquiry_store)
          // 店舗詳細画面から遷移した場合に該当の店舗名を選択済みにする
        } else if (inquiry_store && inquiry_store.id === store.id) {
          setInquiryStore(displayStoreName)
        }
      })
    }
    setDisplayStores(storeList)
  }, [])

  // コンポーネントマウント時に有効なスケジュールで初期化
  useEffect(() => {
    if (schedules) {
      const validSchedules = schedules.filter(isSelectableSchedule)
      setSchedulesForHopeDateTime2(validSchedules)
    }
  }, [schedules])

  const handleHopeDateTime1 = (value) => {
    setSelectedDateTime1(value)
    setHopeDateTime1(value)
    // 第1希望以外の選択可能なスケジュールを第2希望の選択肢として設定
    const filteredSchedules = schedules.filter(isSelectableSchedule).filter((schedule) => schedule.started_at !== value)
    setSchedulesForHopeDateTime2(filteredSchedules)

    // 第1希望と第2希望が同じになった場合は第2希望をクリア
    if (value === selectedDateTime2) {
      setSelectedDateTime2('')
      setHopeDateTime2('')
    }
  }

  const handleHopeDateTime2 = (value) => {
    setSelectedDateTime2(value)
    setHopeDateTime2(value)
  }

  const handleValueChange = (isValid) => {
    setIsValidForm(isValid)
    isValidParams(isValid, isValidCustomContents)
  }

  function updateIsValidCustomContents(isValid: boolean) {
    setIsValidCustomContents(isValid)
    isValidParams(isValidForm, isValid)
  }

  function isValidParams(isTemplateFormValid, isCustomContentValid) {
    // is_flexible_schedule = true の場合は日程バリデーションをスキップ
    if (!isFlexibleSchedule) {
      if (event && displayHopeDateTimeFixed(event) && !oldSpecifiedDate(event)) {
        if (schedules.length === 1) {
          setHopeDateTime1(schedules[0].started_at)
        }
        if (requiredDateTime2()) {
          if (!(hopeDateTime1 && hopeDateTime2)) {
            setIsValidApplicantParams(false)
            return
          }
        } else {
          if (!hopeDateTime1) {
            setIsValidApplicantParams(false)
            return
          }
        }
      }

      // 希望日時を選択しているときの時間選択チェック
      if (event && displayHopeDateTimeFlexible(event) && !oldSpecifiedDate(event)) {
        // 修正後
        if (!(hopeDate1 && hopeDate1Filled)) {
          setIsValidApplicantParams(false)
          return
        }
        if (hopeDate2 && !hopeDate2Filled) {
          setIsValidApplicantParams(false)
          return
        }
        if (requiredDateTime2() && !(hopeDateTime2 && hopeDate2Filled)) {
          setIsValidApplicantParams(false)
          return
        }
      }
    }

    if (!isTemplateFormValid) {
      setIsValidApplicantParams(false)
      return
    }

    if (!isCustomContentValid) {
      setIsValidApplicantParams(false)
      return
    }

    // 表示非表示チェック
    if (applicant_form_setting) {
      if (applicant_form_setting.is_show_number_of_adult && !numberOfAdult) {
        setIsValidApplicantParams(false)
        return
      }
    } else {
      if (event && event.classification === 'study_and_seminar' && !numberOfAdult) {
        setIsValidApplicantParams(false)
        return
      }
    }
    setIsValidApplicantParams(true)
  }

  const isDisplayStoreField = (event, applicant_form_setting) => {
    if (displayStores.length === 0) return false

    if (applicant_form_setting) {
      return applicant_form_setting.is_show_store
    } else {
      return event && is_consultation_event
    }
  }

  const changeNumberOfAdult = (value) => {
    setNumberOfAdult(value)
    setAdultErrorMsg('')
  }

  const [blankCheck, setBlankCheck] = useState(0)
  const [adultErrorMsg, setAdultErrorMsg] = useState('')

  const scroll = window.innerWidth >= 989 ? 20 : 45

  const checkBlank = (e) => {
    if (!isValidApplicantParams) {
      e.preventDefault()
      if (numberOfAdult == 0) {
        setAdultErrorMsg('大人は1以上の値にしてください')
      }
      setBlankCheck(blankCheck + 1)
      setTimeout(() => {
        let firstError = document.getElementsByClassName('error')[0]
        let rectTop = firstError.getBoundingClientRect().top
        let positionY = window.pageYOffset
        let target = rectTop + positionY - scroll
        ScrollError(target)
      }, 10)
    }
  }

  useEffect(() => {
    isValidParams(isValidForm, isValidCustomContents)
  }, [hopeDateTime1, hopeDate1, hopeDate1Filled, hopeDateTime2, hopeDate2, hopeDate2Filled, numberOfAdult, isFlexibleSchedule])

  const handleFlexibleScheduleChange = (checked: boolean) => {
    setIsFlexibleSchedule(checked)
    if (checked) {
      // DatePickerの値をクリア
      setHopeDateTime1('')
      setHopeDate1('')
      setHopeDate1Filled(false)
      setSelectedDateTime1('')
      setHopeDateTime2('')
      setHopeDate2('')
      setHopeDate2Filled(false)
      setSelectedDateTime2('')
    }
  }

  const displayHopeDateTimeFlexible = (event) => {
    if (
      event.schedule_type === 'long_term' ||
      (event.schedule_type === 'specified_date' && event.reservation_time_type === 'flexible')
    ) {
      return true
    }
    return false
  }

  const displayHopeDateTimeFixed = (event) => {
    if (event.schedule_type === 'specified_date' && event.reservation_time_type === 'fixed') {
      return true
    }
    return false
  }

  const oldSpecifiedDate = (event) => {
    if (event.schedule_type === 'specified_date' && schedules.length === 0) {
      return true
    }
    return false
  }

  const displayFixedSchedule = (schedule, withFinishedAt = true) => {
    // 開始時刻を処理
    const startDate = new Date(schedule.started_at)
    const startYear = startDate.getFullYear()
    const startMonth = startDate.getMonth() + 1
    const startDay = startDate.getDate()
    const startDayOfWeekIndex = startDate.getDay()
    const startDayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][startDayOfWeekIndex]
    const startHour = startDate.getHours().toString().padStart(2, '0')
    const startMinute = startDate.getMinutes().toString().padStart(2, '0')

    // 終了時刻を表示しない場合
    if (!withFinishedAt) {
      return `${startYear}年${startMonth}月${startDay}日（${startDayOfWeek}）　${startHour}:${startMinute}`
    }

    // 終了時刻を処理
    const endDate = new Date(schedule.finished_at)
    const endYear = endDate.getFullYear()
    const endMonth = endDate.getMonth() + 1
    const endDay = endDate.getDate()
    const endDayOfWeekIndex = endDate.getDay()
    const endDayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][endDayOfWeekIndex]
    const endHour = endDate.getHours().toString().padStart(2, '0')
    const endMinute = endDate.getMinutes().toString().padStart(2, '0')

    // 開始日と終了日が同じかチェック
    const isSameDate = startYear === endYear && startMonth === endMonth && startDay === endDay

    // フォーマットされた文字列を作成
    let formattedDate = ''

    if (isSameDate) {
      formattedDate = `${startYear}年${startMonth}月${startDay}日（${startDayOfWeek}）　${startHour}:${startMinute} 〜 ${endHour}:${endMinute}`
    } else {
      formattedDate = `${startYear}年${startMonth}月${startDay}日（${startDayOfWeek}）　${startHour}:${startMinute} 〜 ${endYear}年${endMonth}月${endDay}日（${endDayOfWeek}）　${endHour}:${endMinute}`
    }

    return formattedDate
  }

  // 開催済みでないスケジュールかどうかを判定する関数
  const isSelectableSchedule = (schedule) => {
    const now = new Date()
    const scheduleStartDate = new Date(schedule.started_at)
    // 現在時刻より後のスケジュールのみ選択可能
    return scheduleStartDate > now
  }

  return (
    <>
      <div className={styles.form}>
        <div>
          {event && (
            <>
              <input type={'hidden'} name="applicant_form[event_id]" defaultValue={event.id} />
              <input type={'hidden'} name="applicant_form[classification]" value={event.classification} />
              <input type={'hidden'} name="applicant_form[event_type]" value={event.event_type} />
              <input type={'hidden'} name="applicant_form[is_flexible_schedule]" value={String(isFlexibleSchedule)} />
            </>
          )}
          {isDisplayStoreField(event, applicant_form_setting) && (
            <div className={styles.inputContainer}>
              <label className={styles.label}>
                <div className={styles.labelTitle}>問い合せ先店舗</div>
                <div className={styles.labelTag + ' ' + styles.optional}>任意</div>
              </label>
              <div
                className={`${styles.checkboxRadioContainer} ${errors && errors.inquiry_store && styles.errorInput}`}
              >
                {displayStores.map((store, index) => (
                  <div className={styles.checkList} key={index}>
                    <input
                      type="radio"
                      value={store}
                      id={store}
                      name={`applicant_form[inquiry_store]`}
                      checked={store === inquiryStore}
                      onChange={(e) => setInquiryStore(e.target.value)}
                      className={styles.formRadio}
                    />
                    <label htmlFor={store} className={styles.formRadioLabel}>
                      {store}
                    </label>
                  </div>
                ))}
              </div>
              {errors && errors.inquiry_store && (
                <>
                  {errors.inquiry_store.map((error, index) => (
                    <div className={styles.errorText} key={index}>
                      {error}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {applicant_form_setting && applicant_form_setting.is_request_online && (
            <>
              <div className={styles.inputContainer}>
                <label className={styles.label}>
                  <div className={styles.labelTitle}>オンライン希望</div>
                  <div className={styles.labelTag + ' ' + styles.required}>必須</div>
                </label>
                <div
                  className={`${styles.checkboxRadioContainer} ${
                    errors && errors.is_request_online && styles.errorInput
                  }`}
                >
                  <div className={styles.checkList}>
                    <input
                      type="radio"
                      value={'true'}
                      id={`showOnline`}
                      name={`applicant_form[is_request_online]`}
                      defaultChecked={isRequestOnline}
                      onChange={(e) => setIsRequestOnline(e.target.value === 'true')}
                      className={styles.formRadio}
                    />
                    <label htmlFor={`showOnline`} className={styles.formRadioLabel}>
                      オンラインを希望する
                    </label>
                  </div>
                  <div className={styles.checkList}>
                    <input
                      type="radio"
                      value={'false'}
                      id={`showOffline`}
                      name={`applicant_form[is_request_online]`}
                      defaultChecked={!isRequestOnline}
                      onChange={(e) => setIsRequestOnline(e.target.value === 'true')}
                      className={styles.formRadio}
                    />
                    <label htmlFor={`showOffline`} className={styles.formRadioLabel}>
                      店舗での打ち合わせを希望する
                    </label>
                  </div>
                </div>
              </div>
              {errors && errors.is_request_online && (
                <div className={styles.requestOnlineErrors}>
                  {errors.is_request_online.map((error, index) => (
                    <div className={styles.errorText} key={index}>
                      {error}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {event && displayHopeDateTimeFlexible(event) && (
            <div className={styles.inputContainer + ' ' + styles.multipleInput}>
              <div className={styles.hopeDateLabelPc} >
                <label className={styles.label}>
                  <div className={styles.labelTitle}>希望日時</div>
                  <div
                    className={styles.labelTag + ' ' + styles.required}
                    style={isFlexibleSchedule ? { opacity: 0.5 } : {}}
                  >
                    必須
                  </div>
                </label>
                <label className={styles.label + ' ' + styles.selectDateLabel}>
                  <div className={styles.labelTitle}></div>
                  <div className={styles.labelTag + ' ' + (requiredDateTime2() ? styles.required : styles.optional)}
                  style={isFlexibleSchedule ? { opacity: 0.5 } : {}}
                  >
                    {requiredDateTime2() ? '必須' : '任意'}
                  </div>
                </label>
              </div>
              <div className={styles.hopeDateLabelSp}>
                <label className={styles.label}>
                  <div className={styles.labelTitle}>希望日時</div>
                </label>
              </div>
              <div className={styles.multipleInputContainerWrapper}>
                <div className={styles.multipleInputContainer}>
                  <div
                  className={styles.note + ' ' + styles.smallInputTitle}
                  style={isFlexibleSchedule ? { opacity: 0.5 } : {}}
                  >
                    第一希望
                  </div>
                  <div className={styles.hopeDateTimeInputWrapper}>
                    <div
                      className={
                        styles.hopeDateLabelSp + ' ' + styles.spLabel + ' ' + styles.labelTag + ' ' + styles.required
                      }
                    >
                      必須
                    </div>
                    <div className={styles.DatePickerWrapper} style={isFlexibleSchedule ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
                      <DividedDatePicker
                        key={`datetime1-${isFlexibleSchedule}`}
                        selectedDate={isFlexibleSchedule ? '' : selectedDateTime1}
                        startDate={event.holding_at}
                        endDate={event.finished_at}
                        openTime={event.open_time}
                        closeTime={event.close_time}
                        idName={`applicant_form_hope_datetime1_${event.id}`}
                        nameName={isFlexibleSchedule ? '' : 'applicant_form[hope_datetime1]'}
                        className={
                          styles.formInput +
                          ' ' +
                          styles.formSamallInput +
                          ' ' +
                          (errors?.hope_datetime1 ? styles.errorInput : '')
                        }
                        clearable={false}
                        setDateTime={setHopeDateTime1}
                        setDate={setHopeDate1}
                        dateFilled={setHopeDate1Filled}
                        errors={errors && errors.hope_datetime1 ? errors.hope_datetime1 : null}
                        required={!isFlexibleSchedule}
                        check={blankCheck}
                        errorTemp={'第一'}
                        schedule_type={event.schedule_type}
                        closed_wday={event.closed_wday}
                        reservationSlotIntervalMinutes={event.reservation_slot_interval_minutes}
                        schedules={schedules}
                        closed_schedules={closed_schedules}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.multipleInputContainer} style={isFlexibleSchedule ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                  <div className={styles.note + ' ' + styles.smallInputTitle}>第二希望</div>
                  <div className={styles.hopeDateTimeInputWrapper}>
                    <div
                      className={
                        styles.hopeDateLabelSp +
                        ' ' +
                        styles.spLabel +
                        ' ' +
                        styles.labelTag +
                        ' ' +
                        (requiredDateTime2() ? styles.required : styles.optional)
                      }
                    >
                      {requiredDateTime2() ? '必須' : '任意'}
                    </div>
                    <div className={styles.DatePickerWrapper}>
                      <DividedDatePicker
                        key={`datetime2-${isFlexibleSchedule}`}
                        selectedDate={isFlexibleSchedule ? '' : selectedDateTime2}
                        startDate={event.holding_at}
                        endDate={event.finished_at}
                        openTime={event.open_time}
                        closeTime={event.close_time}
                        idName={`applicant_form_hope_datetime2_${event.id}`}
                        nameName={isFlexibleSchedule ? '' : 'applicant_form[hope_datetime2]'}
                        className={
                          styles.formInput +
                          ' ' +
                          styles.formSamallInput +
                          ' ' +
                          (errors?.hope_datetime2 ? styles.errorInput : '')
                        }
                        clearable={!requiredDateTime2()}
                        setDateTime={setHopeDateTime2}
                        setDate={setHopeDate2}
                        dateFilled={setHopeDate2Filled}
                        errors={errors && errors.hope_datetime2 ? errors.hope_datetime2 : null}
                        required={requiredDateTime2() && !isFlexibleSchedule}
                        check={blankCheck}
                        errorTemp={'第二'}
                        schedule_type={event.schedule_type}
                        closed_wday={event.closed_wday}
                        reservationSlotIntervalMinutes={event.reservation_slot_interval_minutes}
                        schedules={schedules}
                        closed_schedules={closed_schedules}
                      />
                    </div>
                  </div>
                </div>
                {/* これ以外の日程を希望チェックボックス */}
                {event.show_flexible_schedule_option && (
                  <div className={styles.checkList + ' ' + styles.flexibleScheduleCheckbox}>
                    <input
                      type="checkbox"
                      id="flexibleScheduleCheckbox"
                      checked={isFlexibleSchedule}
                      onChange={(e) => handleFlexibleScheduleChange(e.target.checked)}
                      className={styles.formCheckbox}
                    />
                    <label htmlFor="flexibleScheduleCheckbox" className={styles.formCheckboxLabel}>
                      その他の日程で調整を希望する
                    </label>
                    {isFlexibleSchedule && (
                      <div className={styles.note + ' ' + styles.flexibleScheduleNote}>
                        ※後日、担当者より日程調整のご連絡をさせていただきます。
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {event && oldSpecifiedDate(event) && (
            <input type="hidden" name="applicant_form[hope_datetime1]" defaultValue={event.holding_at} />
          )}
          {event && displayHopeDateTimeFixed(event) && schedules.length === 1 && !event.show_flexible_schedule_option && (
            // show_flexible_schedule_option=false の場合: 従来通りの自動選択（hidden inputのみ）
            <input type="hidden" name="applicant_form[hope_datetime1]" value={schedules[0].started_at} />
          )}
          {event && displayHopeDateTimeFixed(event) && schedules.length === 1 && event.show_flexible_schedule_option && (
            <div className={styles.inputContainer}>
              <label className={styles.label}>
                <div className={styles.labelTitle}>希望日時</div>
                <div className={styles.labelTag + ' ' + styles.required}>必須</div>
              </label>
              <div className={styles.checkboxRadioContainer}>
                <div className={styles.checkList}>
                  <input
                    type="radio"
                    value="false"
                    id="selectSchedule"
                    checked={!isFlexibleSchedule}
                    onChange={() => {
                      setIsFlexibleSchedule(false)
                      setHopeDateTime1(schedules[0].started_at)
                    }}
                    className={styles.formRadio}
                  />
                  <label htmlFor="selectSchedule" className={styles.formRadioLabel}>
                    {displayFixedSchedule(schedules[0], false)} で申し込む
                  </label>
                </div>
                <div className={styles.checkList}>
                  <input
                    type="radio"
                    value="true"
                    id="flexibleSchedule"
                    checked={isFlexibleSchedule}
                    onChange={() => {
                      setIsFlexibleSchedule(true)
                      setHopeDateTime1('')
                    }}
                    className={styles.formRadio}
                  />
                  <label htmlFor="flexibleSchedule" className={styles.formRadioLabel}>
                    その他の日程で調整を希望する
                  </label>
                </div>
                <div className={styles.note + ' ' + styles.scheduleAdjustmentNote}>
                  ※後日、担当者より日程調整のご連絡をさせていただきます。
                </div>
              </div>
              {/* is_flexible_schedule=false の場合のみ hope_datetime1 を送信 */}
              {!isFlexibleSchedule && (
                <input type="hidden" name="applicant_form[hope_datetime1]" value={schedules[0].started_at} />
              )}
            </div>
          )}
          {event && displayHopeDateTimeFixed(event) && schedules.length > 1 && (
            <div className={`${styles.inputContainer} ${styles.multipleInput}`}>
              {/* 全体のラベル（希望日時） */}
              <div className={`${styles.label} ${styles.dateLabel}`}>
                <div className={styles.labelTitle}>希望日時</div>
              </div>

              <div className={`${styles.multipleInputContainerWrapper}`}>
                {/* 第一希望 */}
                <div className={`${styles.multipleInputContainer} ${styles.dateSelectWrapper}`}>
                  <div className={styles.dateSelectLabel}>
                    <label className={`${styles.note} ${styles.smallInputTitle} ${styles.dateLabelTitle}`} htmlFor="hope_datetime1_select">
                      第一希望
                    </label>
                    <div className={`${styles.labelTag} ${styles.required}`}>必須</div>
                  </div>
                  <div className={styles.hopeDateTimeInputWrapper}>
                    <div className={styles.formSelectBox}>
                      <select
                        id="hope_datetime1_select"
                        name={isFlexibleSchedule ? undefined : 'applicant_form[hope_datetime1]'}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === 'flexible') {
                            setIsFlexibleSchedule(true)
                            setSelectedDateTime1('')
                            setHopeDateTime1('')
                          } else {
                            setIsFlexibleSchedule(false)
                            handleHopeDateTime1(value)
                          }
                        }}
                        value={isFlexibleSchedule ? 'flexible' : selectedDateTime1}
                        className={errors?.hope_datetime1 ? `${styles.errorInput} error` : ''}
                      >
                        <option hidden value="">選択してください</option>
                        {schedules.filter(isSelectableSchedule).map((schedule, index) => (
                          <option key={index} value={schedule.started_at}>
                            {displayFixedSchedule(schedule, false)}
                          </option>
                        ))}
                        {event.show_flexible_schedule_option && (
                          <option value="flexible">その他の日程で調整を希望する</option>
                        )}
                      </select>
                      {isFlexibleSchedule && event.show_flexible_schedule_option && (
                        <div className={`${styles.note} ${styles.scheduleAdjustmentNoteWithMargin}`}>
                          ※後日、担当者より日程調整のご連絡をさせていただきます。
                        </div>
                      )}
                      {errors?.hope_datetime1?.map((error, index) => (
                        <div className={styles.errorText} key={index}>{error}</div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 第二希望 */}
                {schedulesForHopeDateTime2.length > 0 && (
                  <div className={`${styles.multipleInputContainer} ${styles.dateSelectWrapper} ${isFlexibleSchedule ? styles.disabledInput : ''}`}>
                    <div className={styles.dateSelectLabel}>
                      <label className={`${styles.note} ${styles.smallInputTitle} ${styles.dateLabelTitle}`} htmlFor="hope_datetime2_select">
                        第二希望
                      </label>
                      <div className={`${styles.labelTag} ${requiredDateTime2() ? styles.required : styles.optional}`}>
                        {requiredDateTime2() ? '必須' : '任意'}
                      </div>
                    </div>
                    <div className={styles.hopeDateTimeInputWrapper}>
                      <div className={styles.formSelectBox}>
                        <select
                          id="hope_datetime2_select"
                          name="applicant_form[hope_datetime2]"
                          onChange={(e) => handleHopeDateTime2(e.target.value)}
                          value={selectedDateTime2}
                          disabled={isFlexibleSchedule}
                          className={errors?.hope_datetime2 ? `${styles.errorInput} error` : ''}
                        >
                          <option hidden value="">選択してください</option>
                          {schedulesForHopeDateTime2.map((schedule, index) => (
                            <option key={index} value={schedule.started_at}>
                              {displayFixedSchedule(schedule, false)}
                            </option>
                          ))}
                        </select>
                        {errors?.hope_datetime2?.map((error, index) => (
                          <div className={styles.errorText} key={index}>{error}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(!applicant_form_setting ||
            (applicant_form_setting && applicant_form_setting.is_show_number_of_adult) ||
            (applicant_form_setting && applicant_form_setting.is_show_number_of_children)) && (
            <div className={styles.inputContainer + ' ' + styles.multipleInput}>
              <label className={styles.label}>
                <div className={styles.labelTitle}>参加人数</div>
                <div className={styles.labelTag + ' ' + styles.required}>必須</div>
              </label>
              <div>
                {(!applicant_form_setting ||
                  (applicant_form_setting && applicant_form_setting.is_show_number_of_adult)) && (
                  <div className={styles.multipleInputContainer}>
                    <div className={styles.note + ' ' + styles.smallInputTitle}>大人</div>
                    <div className={styles.formSelectBox}>
                      <select
                        name="applicant_form[number_of_adult]"
                        onChange={(e) => changeNumberOfAdult(Number(e.target.value))}
                        value={numberOfAdult}
                        className={errors?.number_of_adult || adultErrorMsg ? styles.errorInput + ' error' : ''}
                      >
                        <option hidden value={''}>
                          選択してください
                        </option>
                        {numberOfAdultOptions.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors && errors.number_of_adult && (
                        <>
                          {errors.number_of_adult.map((error, index) => (
                            <div className={styles.errorText} key={index}>
                              {error}
                            </div>
                          ))}
                        </>
                      )}
                      {adultErrorMsg && <div className={styles.errorText}>{adultErrorMsg}</div>}
                    </div>
                  </div>
                )}
                {(!applicant_form_setting ||
                  (applicant_form_setting && applicant_form_setting.is_show_number_of_children)) && (
                  <div className={styles.multipleInputContainer}>
                    <div className={styles.note + ' ' + styles.smallInputTitle}>こども</div>
                    <div className={styles.formSelectBox}>
                      <select
                        name="applicant_form[number_of_children]"
                        onChange={(e) => setNumberOfChildren(Number(e.target.value))}
                        value={numberOfChildren}
                        className={errors?.number_of_children ? styles.errorInput : ''}
                      >
                        <option hidden value={''}>
                          選択してください
                        </option>
                        {numberOfChildrenOptions.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors && errors.number_of_children && (
                        <>
                          {errors.number_of_children.map((error, index) => (
                            <div className={styles.errorText} key={index}>
                              {error}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <FormTemplate
          prefectures={prefectures}
          common_form={applicant_form}
          errors={errors}
          form_type="applicant_form"
          isFormValid={handleValueChange}
          isShowNameKana={applicant_form_setting ? applicant_form_setting.is_show_name_kana : true}
          isShowAddress={applicant_form_setting ? applicant_form_setting.is_show_address : true}
          isShowCompanyName={applicant_form_setting ? applicant_form_setting.is_show_company_name : false}
          isShowJobTitle={applicant_form_setting ? applicant_form_setting.is_show_job_title : false}
          placeHolderText={applicant_form_setting ? applicant_form_setting.placeholder_text : ''}
          check={blankCheck}
        />
        {custom_contents && (
          <CustomFormContents
            customContents={custom_contents}
            formName="applicant_form[extra_item_answer]"
            errors={errors && errors.extra_item_answer ? errors.extra_item_answer[0] : null}
            updateIsValidCustomContents={updateIsValidCustomContents}
            inputedForm={applicant_form?.extra_item_answer}
            check={blankCheck}
          />
        )}
        <div className={styles.formText}>
          <a href={terms_path} className={styles.link} rel="noopener" target="_blank">
            利用規約
          </a>
          と
          <a href={privacy_path} className={styles.link} rel="noopener" target="_blank">
            プライバシーポリシー
          </a>
          に同意して
        </div>
        <input
          type={'hidden'}
          name="applicant_form[is_show_detail_page]"
          value={
            applicant_form_setting ? String(applicant_form_setting.is_show_applicant_form_on_detail_page) : 'false'
          }
        />
        <input type={'hidden'} name="commit" value="" />
        <div className={styles.submitContainer}>
          <input
            type={'submit'}
            name={'submit'}
            value={`${isValidApplicantParams ? '送信する' : 'まだ入力が完了していません'}`}
            className={`${styles.submitButton} ${
              isValidApplicantParams ? `${styles.submitButtonAbled} submit-color -${builder.theme_color} -primary` : ``
            }`}
            onClick={(e) => checkBlank(e)}
          />
        </div>
      </div>
      {exit_intent_popup && modal_base_element && (
        <ExitIntentPopup
          title={exit_intent_popup.title}
          text={exit_intent_popup.text}
          imageUrl={exit_intent_popup.imageUrl}
          imageAlt={exit_intent_popup.imageAlt}
          appElementName={modal_base_element}
        />
      )}
    </>
  )
}

export default ApplicantForm
